import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import { WIDGET_TEMPLATES, TEMPLATE_CATEGORY_LABELS, getTemplatesByCategory } from '@/data/widget-templates';
import type { WidgetTemplateId, TemplateCategory } from '@/types/ui/widget-styles';
import type { GenieBranding } from '@/types/ui/branding';
import { WidgetTemplateThumbnail } from './WidgetTemplateThumbnail';

interface WidgetStyleCatalogProps {
  selectedTemplate: WidgetTemplateId;
  onSelectTemplate: (id: WidgetTemplateId) => void;
  branding?: GenieBranding;
}

const CATEGORIES: TemplateCategory[] = ['core', 'button-variants', 'themes'];

const WidgetStyleCatalog = ({ selectedTemplate, onSelectTemplate, branding }: WidgetStyleCatalogProps) => {
  const primaryColor = branding?.primaryColor || '#4E9CFF';

  return (
    <div className="space-y-8">
      {CATEGORIES.map((category) => {
        const templates = getTemplatesByCategory(category);

        return (
          <div key={category}>
            {/* Section header - simple text, no icons */}
            <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-4">
              {TEMPLATE_CATEGORY_LABELS[category]}
            </p>

            {/* Template cards - 4 columns */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {templates.map((template) => {
                const isSelected = selectedTemplate === template.id;

                return (
                  <button
                    key={template.id}
                    onClick={() => onSelectTemplate(template.id)}
                    className={cn(
                      'relative text-left transition-all duration-200 rounded-xl overflow-hidden',
                      'focus:outline-none focus:ring-2 focus:ring-offset-2',
                      isSelected
                        ? 'ring-2 ring-genie-primary focus:ring-genie-primary'
                        : 'ring-1 ring-gray-200 hover:ring-gray-300 focus:ring-gray-300'
                    )}
                  >
                    {/* Thumbnail - larger, cleaner */}
                    <div className="aspect-[3/2] bg-gray-50 relative">
                      <WidgetTemplateThumbnail
                        templateId={template.id}
                        primaryColor={primaryColor}
                        config={template.config}
                      />

                      {/* Selected checkmark overlay */}
                      {isSelected && (
                        <div className="absolute top-2 right-2 p-1.5 bg-genie-primary rounded-full shadow-lg">
                          <Check className="h-3 w-3 text-white" strokeWidth={3} />
                        </div>
                      )}

                      {/* Recommended badge */}
                      {template.recommended && !isSelected && (
                        <div className="absolute top-2 right-2 px-2 py-1 bg-gray-900/80 text-white text-[10px] font-medium rounded-md">
                          Recommended
                        </div>
                      )}
                    </div>

                    {/* Template name and description */}
                    <div className={cn(
                      'p-3',
                      isSelected ? 'bg-genie-primary/5' : 'bg-white'
                    )}>
                      <p className={cn(
                        'font-medium text-sm',
                        isSelected ? 'text-genie-primary' : 'text-gray-900'
                      )}>
                        {template.name}
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">
                        {template.description}
                      </p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default WidgetStyleCatalog;
